import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  familyService,
  type FamilyGoal,
  type FamilyLicense,
  type MemberLimitCheck,
} from "@/services/familyService";
import { useAuth } from "@/context/AuthContext";
import { shouldDeferToDashboardBatch } from "@/utils/dashboardQueryCache";
import { useCallback } from "react";
import { type FamilyDashboardStats } from "@/utils/familyDashboardStats";
import {
  getFamilyHubDevPreviewBundle,
  isFamilyHubDevPreviewActive,
} from "@/utils/familyHubDevPreview";
import {
  buildDevPreviewDashboardStatsFromPreview,
  buildDevPreviewMemberLimit,
  buildLocalFamilyDashboardStats,
  fetchFamilyDataForUser,
  mergeRemoteFamilyDashboardStats,
} from "@/hooks/useOptimizedFamilyHelpers";
import type {
  FamilyGoalInput,
  FamilyMemberInput,
  FamilyMemberUpdate,
  PrimaryMemberInput,
} from "@/hooks/useFamilyMutationsHelpers";
import { useFamilyMutations } from "@/hooks/useFamilyMutations";
import {
  loadFamilyDashboardStatsForHook,
  resolveFamilyHookViewModel,
  resolveMemberLimitCheck,
} from "@/hooks/useOptimizedFamilyViewHelpers";

export type { FamilyDashboardStats, MemberLimitCheck };
export type { FamilyGoal } from "@/services/familyService";
export type {
  FamilyGoalInput,
  FamilyMemberInput,
  FamilyMemberUpdate,
  PrimaryMemberInput,
} from "@/hooks/useFamilyMutationsHelpers";

export type FamilyMemberRole = "ADMIN" | "SPOUSE" | "CHILD" | "DEPENDENT";

export interface FamilyMember {
  id: string;
  family_id: string;
  user_id: string | null;
  full_name: string;
  relationship: string;
  pan_number: string;
  encrypted_pan_number?: string | null;
  pan_encryption_iv?: string | null;
  masked_pan?: string | null;
  role: FamilyMemberRole;
  date_of_birth: string | null;
  is_active: boolean;
  is_deceased?: boolean;
  deceased_at?: string | null;
  deceased_reason?: string | null;
  deceased_notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface Family {
  id: string;
  primary_user_id: string;
  name: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface FamilyData {
  family: Family | null;
  familyMembers: FamilyMember[];
  familyGoals: FamilyGoal[];
  familyLicense: FamilyLicense | null;
  loadError?: string | null;
}

/**
 * Optimized hook for fetching family data with React Query caching and deduplication
 */
export const useOptimizedFamily = () => {
  const { user, session } = useAuth();
  const [searchParams] = useSearchParams();
  const isDevPreview = isFamilyHubDevPreviewActive(searchParams.toString());
  const devPreview = isDevPreview ? getFamilyHubDevPreviewBundle() : null;
  const queryClient = useQueryClient();
  const deferToDashboard = shouldDeferToDashboardBatch(queryClient, user?.id);

  const {
    data: familyData,
    isLoading,
    isFetched,
    error,
    refetch,
  } = useQuery({
    queryKey: ["family", user?.id],
    queryFn: (): Promise<FamilyData> => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      return fetchFamilyDataForUser(user.id);
    },
    enabled: Boolean(user?.id && session?.access_token && !deferToDashboard && !isDevPreview),
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const mutations = useFamilyMutations({ userId: user?.id, familyData });

  const getFamilyDashboardStats = useCallback((): Promise<FamilyDashboardStats> => {
    return loadFamilyDashboardStatsForHook({
      isDevPreview,
      previewMemberCount: devPreview?.familyMembers.length ?? 0,
      previewGoalCount: devPreview?.familyGoals.length ?? 0,
      familyData,
      buildLocal: buildLocalFamilyDashboardStats,
      buildPreview: buildDevPreviewDashboardStatsFromPreview,
      mergeRemote: mergeRemoteFamilyDashboardStats,
      fetchRemote: (familyId) => familyService.getFamilyDashboard(familyId),
    });
  }, [devPreview, familyData, isDevPreview]);

  const checkMemberLimit = useCallback((): Promise<MemberLimitCheck> => {
    return resolveMemberLimitCheck({
      isDevPreview,
      previewMemberCount: devPreview?.familyMembers.length ?? 0,
      familyId: familyData?.family?.id,
      buildPreviewLimit: buildDevPreviewMemberLimit,
      checkLimit: (familyId) => familyService.checkMemberLimit(familyId),
    });
  }, [devPreview, familyData?.family?.id, isDevPreview]);

  const view = resolveFamilyHookViewModel({
    isDevPreview,
    devPreview,
    familyData,
    isLoading,
    isFetched,
    error: error ?? null,
    user,
    createPending: mutations.createFamilyMutation.isPending,
    createGoalPending: mutations.createFamilyGoalMutation.isPending,
    addMemberPending: mutations.addFamilyMemberMutation.isPending,
  });

  return {
    ...view,
    refetch,
    loadFamilyData: refetch,
    getFamilyDashboardStats,
    checkMemberLimit,
    createFamily: (familyName: string, primaryMember: PrimaryMemberInput) =>
      mutations.createFamilyMutation.mutateAsync({ familyName, primaryMember }),
    createFamilyGoal: (goalData: FamilyGoalInput) =>
      mutations.createFamilyGoalMutation.mutateAsync(goalData),
    addFamilyMember: (memberData: FamilyMemberInput) =>
      mutations.addFamilyMemberMutation.mutateAsync(memberData),
    archiveFamilyMember: (memberId: string) =>
      mutations.archiveFamilyMemberMutation.mutateAsync(memberId),
    deleteFamilyMember: (memberId: string) =>
      mutations.deleteFamilyMemberMutation.mutateAsync(memberId),
    updateFamily: (updates: { name: string }) =>
      mutations.updateFamilyMutation.mutateAsync(updates),
    deleteFamily: () => mutations.deleteFamilyMutation.mutateAsync(),
    updateFamilyMember: (memberId: string, updates: FamilyMemberUpdate) =>
      mutations.updateFamilyMemberMutation.mutateAsync({ memberId, updates }),
  };
};
